import { useEffect, useState } from "react";
import "./PrivacyPolicy.css";

const tocItems = [
  { id: "introduction", label: "Introduction" },
  { id: "summary", label: "1. Summary of Key Points" },
  { id: "information", label: "2. What Information Do We Collect?" },
  { id: "processing", label: "3. How Do We Process Your Information?" },
  {
    id: "legal-bases",
    label: "4. Legal Bases for Processing Your Information",
  },
  {
    id: "sharing",
    label: "5. When and With Whom Do We Share Your Personal Information?",
  },
  {
    id: "security",
    label: "6. Data Security: How We Protect Your Information",
  },
  { id: "retention", label: "7. Data Retention" },
  { id: "minors", label: "8. Information from Minors" },
  {
    id: "canada",
    label: "9. Your Canadian Privacy Rights (PIPEDA & Provincial Laws)",
  },
  {
    id: "us",
    label: "10. Your United States Privacy Rights (HIPAA & State Laws)",
  },
  { id: "tracking", label: "11. Controls for Do-Not-Track Features" },
  { id: "updates", label: "12. Updates to This Policy" },
  { id: "contact", label: "13. How to Contact Us" },
];

export default function PrivacyPolicy() {
  const [activeId, setActiveId] = useState("introduction");

  useEffect(() => {
    const sectionElements = tocItems
      .map((item) => document.getElementById(item.id))
      .filter(Boolean);

    const initialId =
      window.location.hash && document.querySelector(window.location.hash)
        ? window.location.hash.slice(1)
        : "introduction";

    setActiveId(initialId);

    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-25% 0px -65% 0px",
        threshold: 0,
      }
    );

    sectionElements.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleTocClick = (event, id) => {
    event.preventDefault();

    const target = document.getElementById(id);
    if (!target) return;

    const offset = target.getBoundingClientRect().top + window.pageYOffset - 24;

    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });

    setActiveId(id);
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <main className="pp-page">
      <div className="pp-container">
        <section className="pp-hero">
          <div className="pp-eyebrow">
            <span className="pp-dot"></span>
            FYMAB Inc. or Mindsherpa Privacy Policy
          </div>

          <h1 className="pp-title">Privacy Policy</h1>

          <p className="pp-subtitle">
            This policy explains how FYMAB Inc. or Mindsherpa.health collects,
            stores, uses, shares, protects, and retains personal information
            when you use Mindsherpa services.
          </p>

          <div className="pp-meta">
            <span className="pp-pill">Effective: August 18, 2025</span>
            <span className="pp-pill">Last Updated: August 18, 2025</span>
          </div>
        </section>

        <div className="pp-layout">
          <aside className="pp-toc" aria-label="Privacy policy contents">
            <p className="pp-toc-title">Contents</p>

            <ol className="pp-toc-list">
              {tocItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className={activeId === item.id ? "pp-active" : ""}
                    aria-current={activeId === item.id ? "true" : undefined}
                    onClick={(event) => handleTocClick(event, item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          <article className="pp-card">
            <div className="pp-intro">
              This Privacy Policy for FYMAB Inc or mindsherpa.health
              (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) describes
              how and why we might collect, store, use, and/or share
              (&quot;process&quot;) your personal information when you use our
              services (&quot;Services&quot;, or &quot;FYMAB Inc&quot; or
              &quot;Mindsherpa&quot;).
            </div>

            <section className="pp-section" id="introduction">
              <h2>Introduction</h2>

              <p>This policy applies when you:</p>

              <ul>
                <li>
                  Visit our website at{" "}
                  <a href="https://www.mindsherpa.health/">
                    https://www.mindsherpa.health/
                  </a>
                  , or any of our other websites that link to this Privacy
                  Policy.
                </li>
                <li>
                  Download and use the Mindsherpa mobile application, an
                  employee mind and body wellness platform.
                </li>
                <li>
                  Engage with us in other related ways, including sales,
                  marketing, or events.
                </li>
              </ul>

              <p>
                Reading this Privacy Policy will help you understand your
                privacy rights and choices. We are dedicated to protecting your
                personal information and your right to privacy. If you do not
                agree with our policies and practices, please do not use our
                Services. If you still have any questions or concerns, please
                contact our Privacy Officer at{" "}
                <a href="mailto:privacy@mindsherpa.health">
                  privacy@mindsherpa.health
                </a>
                .
              </p>
            </section>

            <section className="pp-section" id="summary">
              <h2>1. Summary of Key Points</h2>

              <p>
                This summary provides key points from our Privacy Policy. You
                can find more details by using the table of contents to navigate
                to the relevant sections.
              </p>

              <ul>
                <li>
                  <strong>What personal information do we process?</strong> We
                  process personal, usage, and sensitive health-related
                  information depending on how you interact with our Services.
                </li>
                <li>
                  <strong>Do we process sensitive personal information?</strong>{" "}
                  Yes. As a health and wellness application, we process
                  sensitive data, including health information. In the United
                  States, this data may be considered Protected Health
                  Information (PHI) under HIPAA. We process this information
                  with your explicit consent and in accordance with applicable
                  laws.
                </li>
                <li>
                  <strong>How do we process your information?</strong> We
                  process your information to provide, improve, and administer
                  our Services, communicate with you, for security and fraud
                  prevention, and to comply with the law. We do not share your
                  individual personal data with your employer. Reports provided
                  to employers contain only aggregated and anonymized data.
                </li>
                <li>
                  <strong>With whom do we share personal information?</strong>{" "}
                  We may share information with third-party service providers
                  who help us operate our Services, when required by law, or as
                  part of a business transfer. We do not sell your personal
                  information.
                </li>
                <li>
                  <strong>How do we keep your information safe?</strong> We use
                  a combination of organizational and technical security
                  measures, including encryption and access controls, to protect
                  your personal information, in line with industry best
                  practices and legal requirements like HIPAA.
                </li>
                <li>
                  <strong>What are your rights?</strong> Depending on your
                  geographical location, you have specific rights regarding your
                  personal information under laws like Canada&apos;s PIPEDA and
                  various US state and federal laws, including HIPAA.
                </li>
                <li>
                  <strong>How can you exercise your rights?</strong> You can
                  exercise your rights by contacting us at{" "}
                  <a href="mailto:info@mindsherpa.health">
                    info@mindsherpa.health
                  </a>
                  . We will respond to all requests in accordance with
                  applicable data protection laws.
                </li>
              </ul>
            </section>

            <section className="pp-section" id="information">
              <h2>2. What Information Do We Collect?</h2>

              <p>
                We collect Personal Data when you visit our website, download
                our mobile application, or use our Services. The chart below
                details the categories of Personal Data we may collect.
              </p>

              <div className="pp-table-wrap">
                <table className="pp-table">
                  <thead>
                    <tr>
                      <th>Category of Personal Data</th>
                      <th>Examples</th>
                      <th>Purpose(s) for Collection</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>
                        <strong>Account &amp; Contact Data</strong>
                      </td>
                      <td>
                        First and last name, email address, username, password.
                      </td>
                      <td>
                        To create and manage your account, provide Services, and
                        communicate with you.
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>Payment Data</strong>
                      </td>
                      <td>
                        Credit/debit card number, billing address. Processed by
                        a third-party payment processor.
                      </td>
                      <td>To process payments for subscription services.</td>
                    </tr>

                    <tr>
                      <td>
                        <strong>Device &amp; Usage Data</strong>
                      </td>
                      <td>
                        IP address, device ID, browser type, operating system,
                        usage statistics, interaction with features.
                      </td>
                      <td>
                        To provide and improve the Services, for security, and
                        to identify usage trends.
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>
                          Sensitive Personal Information &amp; Protected Health
                          Information (PHI)
                        </strong>
                      </td>
                      <td>
                        Health data you provide, responses to wellness surveys,
                        usage of mental health exercises, information about
                        physical or mental health conditions, race or ethnic
                        origin.
                      </td>
                      <td>
                        To personalize your wellness program, track your
                        progress, and provide the core functionality of the
                        Services. This is done only with your explicit consent.
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>Employer-Provided Data</strong>
                      </td>
                      <td>
                        Your name and work email may be provided by your
                        employer to confirm eligibility for our Services.
                      </td>
                      <td>
                        To facilitate your enrollment in the employer-sponsored
                        wellness program.
                      </td>
                    </tr>

                    <tr>
                      <td>
                        <strong>User-Generated Content</strong>
                      </td>
                      <td>
                        Information you voluntarily provide in free-form fields,
                        journal entries, or communications with us.
                      </td>
                      <td>
                        To provide the features you use and to respond to your
                        inquiries.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="pp-section" id="processing">
              <h2>3. How Do We Process Your Information?</h2>

              <p>We process your information for the following reasons:</p>

              <ul>
                <li>
                  <strong>To Provide and Manage the Services:</strong> We use
                  your data to facilitate account creation, manage your account,
                  and deliver the core features of the Mindsherpa wellness
                  program.
                </li>
                <li>
                  <strong>To Personalize Your Experience:</strong> We use your
                  health and usage data to tailor content and wellness
                  recommendations to you.
                </li>
                <li>
                  <strong>To Communicate With You:</strong> We may use your
                  contact information to send you service-related updates,
                  security alerts, and support messages, or to request feedback.
                </li>
                <li>
                  <strong>To Provide Anonymized Reporting to Employers:</strong>{" "}
                  We process user data to create aggregated and anonymized
                  reports for employers. These reports show general trends in
                  workforce well-being, such as &quot;25% of users engaged with
                  stress-reduction modules this month,&quot; and never contain
                  your individual personal information.
                </li>
                <li>
                  <strong>For Security and Fraud Prevention:</strong> We process
                  information to protect our Services, our users, and our company
                  from security threats and fraudulent activity.
                </li>
                <li>
                  <strong>To Comply with Legal Obligations:</strong> We may
                  process your information to comply with applicable laws,
                  respond to legal requests from law enforcement or government
                  bodies, and to establish or defend our legal rights.
                </li>
                <li>
                  <strong>To Improve Our Services:</strong> We analyze usage
                  trends to understand how our users interact with our Services
                  so we can improve them.
                </li>
              </ul>
            </section>

            <section className="pp-section" id="legal-bases">
              <h2>4. Legal Bases for Processing Your Information</h2>

              <p>
                We only process your personal information when we have a valid
                legal reason to do so under applicable law. This includes:
              </p>

              <ul>
                <li>
                  <strong>Consent:</strong> We rely on your explicit consent to
                  process sensitive personal information and to send you
                  marketing communications. You can withdraw your consent at any
                  time.
                </li>
                <li>
                  <strong>Contractual Obligations:</strong> We process personal
                  information to fulfill our contractual obligations to you,
                  including providing the Services as described in our Terms of
                  Service.
                </li>
                <li>
                  <strong>Legitimate Business Interests:</strong> We process
                  information for our legitimate interests, such as improving our
                  Services and for security purposes, provided these interests
                  are not overridden by your rights and interests.
                </li>
                <li>
                  <strong>Legal Obligations:</strong> We process information to
                  comply with our legal and regulatory obligations.
                </li>
              </ul>
            </section>

            <section className="pp-section" id="sharing">
              <h2>5. When and With Whom Do We Share Your Personal Information?</h2>

              <p>
                We may share your personal information in the following
                situations:
              </p>

              <ul>
                <li>
                  <strong>Third-Party Service Providers:</strong> We share
                  information with vendors, consultants, and other third-party
                  service providers who perform services on our behalf, such as
                  cloud hosting, payment processing, and data analytics. These
                  providers are contractually obligated to protect your data and
                  can only use it for the purposes we specify. For example, we
                  use Amazon Web Services (AWS) and Google Cloud for cloud
                  hosting, Stripe for payment processing, and Google Analytics
                  for website analytics.
                </li>
                <li>
                  <strong>Anonymized Data with Employers:</strong> We only share
                  aggregated and anonymized data with the employer sponsoring
                  your access to our Services.
                </li>
                <li>
                  <strong>Business Transfers:</strong> We may share or transfer
                  your information in connection with, or during negotiations of,
                  any merger, sale of company assets, financing, or acquisition
                  of all or a portion of our business to another company.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose your
                  information where we are legally required to do so in order to
                  comply with applicable law, a court order, or other
                  governmental request.
                </li>
              </ul>
            </section>

            <section className="pp-section" id="security">
              <h2>6. Data Security: How We Protect Your Information</h2>

              <p>
                We have implemented robust administrative, technical, and
                physical security measures designed to protect the security of
                any personal information we process. Our measures are designed
                to meet the stringent requirements of privacy laws like HIPAA and
                include:
              </p>

              <ul>
                <li>
                  <strong>Encryption:</strong> All data, including Protected
                  Health Information (PHI), is encrypted both in transit using
                  TLS and at rest.
                </li>
                <li>
                  <strong>Access Control:</strong> Access to personal
                  information is strictly limited to authorized personnel who
                  have a legitimate need to access it to perform their job
                  functions.
                </li>
                <li>
                  <strong>Regular Audits:</strong> We conduct regular security
                  risk assessments and vulnerability scanning to identify and
                  remediate potential threats.
                </li>
                <li>
                  <strong>Employee Training:</strong> Our employees receive
                  regular training on data privacy and security protocols.
                </li>
                <li>
                  <strong>Data Breach Notification:</strong> In the event of a
                  data breach, we will notify affected individuals and
                  authorities as required by applicable laws, including PIPEDA
                  and HIPAA.
                </li>
              </ul>

              <p>
                However, no electronic transmission over the internet or
                information storage technology can be guaranteed to be 100%
                secure. While we will do our best to protect your personal
                information, transmission of personal information to and from our
                Services is at your own risk.
              </p>
            </section>

            <section className="pp-section" id="retention">
              <h2>7. Data Retention</h2>

              <p>
                We retain your personal information for as long as it is
                necessary to provide the Services to you and to fulfill the
                purposes outlined in this Privacy Policy. We have specific data
                retention policies that dictate how long we hold onto
                information. For example, we generally delete or anonymize
                personal information within 90 days of a user&apos;s account
                termination, unless a longer retention period is required by
                law, such as for tax, legal, or accounting purposes.
              </p>
            </section>

            <section className="pp-section" id="minors">
              <h2>8. Information from Minors</h2>

              <p>
                Our Services are not intended for use by individuals under the
                age of 18. We do not knowingly collect personal information from
                children under 18. If we become aware that we have collected
                personal information from a child under 18, we will take steps
                to delete that information.
              </p>
            </section>

            <section className="pp-section" id="canada">
              <h2>9. Your Canadian Privacy Rights (PIPEDA &amp; Provincial Laws)</h2>

              <p>
                If you are a resident of Canada, you have specific rights
                regarding your personal information under the Personal
                Information Protection and Electronic Documents Act (PIPEDA) and
                applicable provincial privacy legislation, such as Alberta&apos;s
                and British Columbia&apos;s PIPA and Quebec&apos;s Act
                respecting the protection of personal information in the private
                sector (Law 25). These rights include:
              </p>

              <ul>
                <li>
                  <strong>The Right to Access:</strong> You have the right to
                  request access to the personal information we hold about you.
                </li>
                <li>
                  <strong>The Right to Correction (Rectification):</strong> You
                  have the right to request the correction of inaccurate or
                  incomplete personal information.
                </li>
                <li>
                  <strong>The Right to Withdraw Consent:</strong> You have the
                  right to withdraw your consent to our processing of your
                  personal information at any time, subject to legal or
                  contractual restrictions.
                </li>
              </ul>

              <p>To exercise these rights, please contact our Privacy Officer.</p>
            </section>

            <section className="pp-section" id="us">
              <h2>10. Your United States Privacy Rights (HIPAA &amp; State Laws)</h2>

              <p>
                If you are a resident of the United States, your rights are
                governed by various federal and state laws.
              </p>

              <h3>Health Insurance Portability and Accountability Act (HIPAA)</h3>

              <p>
                Because our Services involve the processing of health information
                and are offered through employer-sponsored wellness programs, we
                are considered a &quot;Business Associate&quot; under HIPAA. We
                are legally obligated to protect your &quot;Protected Health
                Information&quot; (PHI) in accordance with HIPAA&apos;s
                requirements. Your rights regarding your PHI under HIPAA
                include:
              </p>

              <ul>
                <li>
                  <strong>The Right to Access:</strong> You can request a copy
                  of your PHI that we maintain.
                </li>
                <li>
                  <strong>The Right to Amend:</strong> You can request that we
                  amend any inaccurate or incomplete PHI.
                </li>
                <li>
                  <strong>The Right to an Accounting of Disclosures:</strong>{" "}
                  You can request an accounting of certain disclosures we have
                  made of your PHI.
                </li>
              </ul>

              <h3>State-Specific Privacy Rights (e.g., CCPA/CPRA)</h3>

              <p>
                Residents of states like California, Virginia, Colorado, and
                others have additional rights, which may include:
              </p>

              <ul>
                <li>The right to know what personal information is being collected.</li>
                <li>The right to delete personal information.</li>
                <li>
                  The right to opt-out of the &quot;sale&quot; or
                  &quot;sharing&quot; of personal information. For the record,
                  we do not sell your personal information.
                </li>
                <li>
                  The right to non-discrimination for exercising your privacy
                  rights.
                </li>
              </ul>

              <p>
                To exercise any of your US privacy rights, please contact our
                Privacy Officer.
              </p>
            </section>

            <section className="pp-section" id="tracking">
              <h2>11. Controls for Do-Not-Track Features &amp; Cookies</h2>

              <p>
                <strong>Do-Not-Track:</strong> Most web browsers and some mobile
                operating systems include a Do-Not-Track (&quot;DNT&quot;)
                feature or setting you can activate to signal your privacy
                preference not to have data about your online browsing
                activities monitored and collected. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized. As such, we do not currently respond to DNT
                browser signals or any other mechanism that automatically
                communicates your choice not to be tracked online.
              </p>

              <p>
                <strong>Cookies and Other Tracking Technologies:</strong> We use
                cookies and similar tracking technologies to collect and store
                your information. This helps us provide and improve our Services
                and remember your preferences. You can typically manage your
                cookie preferences through your web browser&apos;s settings.
                However, disabling certain cookies may affect the functionality
                of our Services.
              </p>
            </section>

            <section className="pp-section" id="updates">
              <h2>12. Updates to This Policy</h2>

              <p>
                We may update this Privacy Policy from time to time. The updated
                version will be indicated by a &quot;Last Updated&quot; date at
                the top of this policy. If we make material changes, we may
                notify you either by prominently posting a notice of such changes
                or by directly sending you a notification. We encourage you to
                review this Privacy Policy frequently to stay informed of how we
                are protecting your information.
              </p>
            </section>

            <section className="pp-section" id="contact">
              <h2>13. How to Contact Us</h2>

              <p>
                If you have questions, comments, or wish to exercise your
                privacy rights, you may contact our Privacy Officer by email or
                post:
              </p>

              <div className="pp-contact">
                <p>
                  <strong>Privacy Officer</strong>
                  <br />
                  FYMAB Inc. or mindsherpa
                  <br />
                  Unit# 221 1854 Carling Avenue
                  <br />
                  Ottawa, Ontario K2A 1E3
                  <br />
                  Canada
                </p>

                <p>
                  Email:{" "}
                  <a href="mailto:privacy@mindsherpa.health">
                    privacy@mindsherpa.health
                  </a>
                </p>
              </div>
            </section>
          </article>
        </div>
      </div>
    </main>
  );
}